import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Button,
  useTheme,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Fade,
  Backdrop,
  Popover,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar,
  Checkbox
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  ViewDay as ViewDayIcon,
  ViewWeek as ViewWeekIcon,
  ViewModule as ViewModuleIcon,
  DateRange as DateRangeIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  startOfDay,
  endOfDay,
  startOfYear,
  endOfYear,
  addDays, 
  subDays,
  addMonths, 
  subMonths, 
  addWeeks,
  subWeeks,
  addYears,
  subYears,
  isSameMonth, 
  isSameDay, 
  isToday,
  getHours,
  setHours,
  eachHourOfInterval
} from 'date-fns';
import { supabase } from '../../../supabaseClient';

const priorityColors = {
  low: '#4caf50',
  medium: '#ff9800',
  high: '#f44336',
  urgent: '#9c27b0'
};

const CalendarContainer = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.grey[50], 0.5)})`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`,
  }
}));

// Profile Avatar Component
const ProfileAvatar = ({ userId, user, size = 28, sx = {} }) => {
  const [avatarUrl, setAvatarUrl] = useState('');
  
  useEffect(() => {
    if (!userId) return;
    
    const loadAvatar = async () => {
      try {
        const { data: files, error } = await supabase.storage
          .from('profile-avatars')
          .list(`${userId}/`, {
            limit: 1,
            sortBy: { column: 'created_at', order: 'desc' }
          });

        if (error || !files || files.length === 0) return;

        const fileName = `${userId}/${files[0].name}`;
        const { data, error: signedUrlError } = await supabase.storage
          .from('profile-avatars')
          .createSignedUrl(fileName, 3600);
        
        if (!signedUrlError && data?.signedUrl) {
          setAvatarUrl(data.signedUrl);
        }
      } catch (err) {
        // Silent fail
      }
    };

    loadAvatar();
  }, [userId]);

  const getInitials = () => {
    if (user?.full_name) {
      return user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return '?';
  };

  return (
    <Avatar
      src={avatarUrl}
      sx={{ 
        width: size, 
        height: size, 
        fontSize: `${size * 0.4}px`,
        backgroundColor: 'grey.400',
        color: 'white',
        fontWeight: 500,
        ...sx
      }}
    >
      {getInitials()}
    </Avatar>
  );
};

const CalendarView = ({ tasks, onTaskClick, onDateClick, onTaskDelete, timeFormat = '24hour' }) => {
  const theme = useTheme();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('month');
  
  // Quick add state
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickTaskTitle, setQuickTaskTitle] = useState('');
  const [quickTaskDeadline, setQuickTaskDeadline] = useState(null);
  const [quickTaskPriority, setQuickTaskPriority] = useState('medium');
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Day tasks popover state
  const [dayTasksOpen, setDayTasksOpen] = useState(false);
  const [dayTasksAnchor, setDayTasksAnchor] = useState(null);
  const [selectedDayTasks, setSelectedDayTasks] = useState([]);
  
  // Bulk delete state
  const [selectedTasksForDelete, setSelectedTasksForDelete] = useState([]);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);

  const navigateNext = () => {
    switch (currentView) {
      case 'day':
        setCurrentDate(addDays(currentDate, 1));
        break;
      case 'week':
        setCurrentDate(addWeeks(currentDate, 1));
        break;
      case 'month':
        setCurrentDate(addMonths(currentDate, 1));
        break;
      case 'year':
        setCurrentDate(addYears(currentDate, 1));
        break;
    }
  };

  const navigatePrev = () => {
    switch (currentView) {
      case 'day':
        setCurrentDate(subDays(currentDate, 1));
        break;
      case 'week':
        setCurrentDate(subWeeks(currentDate, 1));
        break;
      case 'month':
        setCurrentDate(subMonths(currentDate, 1));
        break;
      case 'year':
        setCurrentDate(subYears(currentDate, 1));
        break;
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleQuickAddOpen = (date) => {
    setSelectedDate(date);
    setQuickTaskDeadline(dayjs(date));
    setQuickAddOpen(true);
  };

  const handleDayClick = (date, event) => {
    handleQuickAddOpen(date);
  };

  const handleDayTasksClose = () => {
    setDayTasksOpen(false);
    setDayTasksAnchor(null);
    setSelectedDayTasks([]);
    setBulkDeleteMode(false);
    setSelectedTasksForDelete([]);
  };

  const handleCreateFromPopover = () => {
    handleDayTasksClose();
    handleQuickAddOpen(selectedDate);
  };

  const toggleBulkDeleteMode = () => {
    setBulkDeleteMode(!bulkDeleteMode);
    setSelectedTasksForDelete([]);
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTasksForDelete(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const selectAllTasks = () => {
    if (selectedTasksForDelete.length === selectedDayTasks.length) {
      setSelectedTasksForDelete([]);
    } else {
      setSelectedTasksForDelete(selectedDayTasks.map(task => task.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTasksForDelete.length > 0) {
      try {
        if (onTaskDelete) {
          // Use parent's delete function if provided
          for (const taskId of selectedTasksForDelete) {
            await onTaskDelete(taskId);
          }
        } else {
          // Direct Supabase delete if no parent function
          const { error } = await supabase
            .from('tasks')
            .delete()
            .in('id', selectedTasksForDelete);
          
          if (error) {
            console.error('Error deleting tasks:', error);
            return;
          }
        }
        
        setBulkDeleteMode(false);
        setSelectedTasksForDelete([]);
        handleDayTasksClose();
        
        // Force a page refresh to update the tasks list
        window.location.reload();
      } catch (error) {
        console.error('Error deleting tasks:', error);
      }
    }
  };

  const handleQuickAdd = () => {
    if (!quickTaskTitle.trim()) return;
    
    // Determine which column based on date
    const today = new Date();
    const oneWeek = new Date(today);
    oneWeek.setDate(today.getDate() + 7);
    const oneMonth = new Date(today);
    oneMonth.setMonth(today.getMonth() + 1);

    let columnType = 'later';
    if (selectedDate <= today) {
      columnType = 'today';
    } else if (selectedDate <= oneWeek) {
      columnType = 'this_week';
    } else if (selectedDate <= oneMonth) {
      columnType = 'this_month';
    }
    
    const quickTaskData = {
      title: quickTaskTitle.trim(),
      description: '',
      priority: quickTaskPriority,
      due_date: quickTaskDeadline ? quickTaskDeadline.toISOString() : null,
      start_date: null,
      column_type: columnType,
      tags: [],
      estimated_hours: null,
      status: 'todo'
    };
    
    onDateClick(quickTaskData);
    handleQuickAddClose();
  };

  const handleQuickAddClose = () => {
    setQuickAddOpen(false);
    setQuickTaskTitle('');
    setQuickTaskDeadline(null);
    setQuickTaskPriority('medium');
    setSelectedDate(null);
  };

  const getViewTitle = () => {
    switch (currentView) {
      case 'day':
        return format(currentDate, 'EEEE, MMMM d, yyyy');
      case 'week':
        const weekStart = startOfWeek(currentDate);
        const weekEnd = endOfWeek(currentDate);
        return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
      case 'month':
        return format(currentDate, 'MMMM yyyy');
      case 'year':
        return format(currentDate, 'yyyy');
      default:
        return format(currentDate, 'MMMM yyyy');
    }
  };

  const renderCalendarContent = () => {
    switch (currentView) {
      case 'day':
        return renderDayView();
      case 'week':
        return renderWeekView();
      case 'month':
        return renderMonthView();
      case 'year':
        return renderYearView();
      default:
        return renderMonthView();
    }
  };

  const renderDayView = () => {
    const dayStart = startOfDay(currentDate);
    const dayEnd = endOfDay(currentDate);
    const hours = eachHourOfInterval({ start: dayStart, end: dayEnd });
    
    const dayTasks = tasks.filter(task => 
      task.due_date && isSameDay(new Date(task.due_date), currentDate)
    );

    return (
      <Box sx={{ p: 0 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '80px 1fr', maxHeight: '75vh', overflow: 'hidden' }}>
          {/* Time column */}
          <Box sx={{ pt: 1 }}>
            {hours.map((hour) => (
              <Box
                key={hour}
                sx={{
                  height: 40,
                  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  color: 'text.secondary'
                }}
              >
                {format(hour, timeFormat === '24hour' ? 'HH:mm' : 'h:mm a')}
              </Box>
            ))}
          </Box>
          
          {/* Tasks column */}
          <Box sx={{ position: 'relative', pt: 1 }}>
            {hours.map((hour) => (
              <Box
                key={hour}
                sx={{
                  height: 40,
                  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                  borderLeft: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                  }
                }}
                onClick={(e) => handleDayClick(hour, e)}
              />
            ))}
            
            {/* Render tasks */}
            {(() => {
              const tasksByHour = {};
              dayTasks.forEach((task, originalIndex) => {
                const taskHour = getHours(new Date(task.due_date));
                if (!tasksByHour[taskHour]) tasksByHour[taskHour] = [];
                tasksByHour[taskHour].push({...task, originalIndex});
              });

              return Object.entries(tasksByHour)
                .sort(([a], [b]) => parseInt(a) - parseInt(b))
                .map(([hour, hourTasks]) => {
                  // Sort tasks within each hour by their original order
                  hourTasks.sort((a, b) => a.originalIndex - b.originalIndex);
                  
                const top = (parseInt(hour) * 40) + 8;
                // Calculate actual available width based on the calendar container
                const containerElement = document.querySelector('[data-calendar-container]');
                let availableWidth;
                if (containerElement) {
                  const containerRect = containerElement.getBoundingClientRect();
                  availableWidth = Math.max(containerRect.width - 120, 400);
                } else {
                  // Fallback if container not found
                  availableWidth = Math.max(window.innerWidth - 200, 400);
                }
                
                let totalWidth = 0;
                let maxVisible = 0;
                
                // Calculate how many tasks can fit
                for (let i = 0; i < hourTasks.length; i++) {
                  const taskWidth = Math.min(Math.max(hourTasks[i].title.length * 7 + 24, 80), 200);
                  const gapWidth = i > 0 ? 6 : 0;
                  const needsPlusButton = i < hourTasks.length - 1; // Not the last task
                  const plusButtonWidth = needsPlusButton ? 76 : 0; // 60px + 6px gap + 10px margin
                  
                  if (totalWidth + taskWidth + gapWidth + plusButtonWidth <= availableWidth) {
                    totalWidth += taskWidth + gapWidth;
                    maxVisible++;
                  } else {
                    // If we can't fit this task, try to fit at least the + button
                    if (maxVisible > 0 && totalWidth + 76 <= availableWidth) {
                      break; // Show + button
                    } else if (maxVisible === 0) {
                      maxVisible = 1; // Show at least one task, even if it gets cut off
                    }
                    break;
                  }
                }
                
                // If we fit all tasks, no need for + button
                if (maxVisible >= hourTasks.length) {
                  maxVisible = hourTasks.length;
                }
                
                const visibleTasks = hourTasks.slice(0, maxVisible);
                const hiddenTasks = hourTasks.slice(maxVisible);
                const hiddenCount = hiddenTasks.length;

                return (
                  <Box key={hour} sx={{ position: 'absolute', top: `${top}px`, left: 8, right: 8, height: 40, pointerEvents: 'none' }}>
                    {visibleTasks.map((task, index) => {
                      const taskWidth = Math.min(Math.max(task.title.length * 7 + 24, 80), 200);
                      let leftPosition = 0;
                      
                      // Calculate position to avoid overlap
                                              for (let i = 0; i < index; i++) {
                          const prevTaskWidth = Math.min(Math.max(visibleTasks[i].title.length * 7 + 24, 80), 200);
                          leftPosition += prevTaskWidth + 6;
                        }
                      
                      return (
                        <Box
                          key={task.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            onTaskClick(task);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: `${leftPosition}px`,
                            width: `${taskWidth}px`,
                            height: 32,
                            backgroundColor: alpha(priorityColors[task.priority], 0.9),
                            color: 'white',
                            borderRadius: 2,
                            px: 1,
                            py: 0.5,
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            zIndex: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            whiteSpace: 'nowrap',
                            gap: 0.5,
                            pointerEvents: 'auto'
                          }}
                        >
                          <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {task.title}
                          </Box>
                          {task.collaborators?.length > 0 && (
                            <GroupIcon sx={{ fontSize: 12, opacity: 0.8 }} />
                          )}
                        </Box>
                      );
                    })}
                    
                    {hiddenCount > 0 && (() => {
                      // Calculate where to place the + button (after the last visible task)
                      let buttonLeftPosition = 0;
                      for (let i = 0; i < visibleTasks.length; i++) {
                        const taskWidth = Math.min(Math.max(visibleTasks[i].title.length * 7 + 24, 80), 200);
                        buttonLeftPosition += taskWidth + (i > 0 ? 6 : 0);
                      }
                      if (visibleTasks.length > 0) {
                        buttonLeftPosition += 6; // Gap before + button
                      }
                      
                      return (
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            // Show only the hidden tasks in the popup
                            setSelectedDayTasks(hiddenTasks);
                            setSelectedDate(new Date());
                            setDayTasksAnchor(e.currentTarget);
                            setDayTasksOpen(true);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: `${buttonLeftPosition}px`,
                            width: 60,
                            height: 32,
                            backgroundColor: alpha(theme.palette.grey[500], 0.9),
                            color: 'white',
                            borderRadius: 2,
                            cursor: 'pointer',
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                            pointerEvents: 'auto'
                          }}
                        >
                          +{hiddenCount}
                        </Box>
                      );
                    })()}
                  </Box>
                );
              });
            })()}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate);
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const hours = eachHourOfInterval({ 
      start: setHours(weekStart, 0), 
      end: setHours(weekStart, 23) 
    });

    return (
      <Box sx={{ p: 0 }}>
        {/* Week header */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '80px repeat(7, 1fr)', borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}` }}>
          <Box />
          {weekDays.map((day) => (
            <Box
              key={day}
              sx={{
                p: 1.5,
                textAlign: 'center',
                borderLeft: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                backgroundColor: isToday(day) ? alpha(theme.palette.primary.main, 0.1) : 'transparent'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, color: isToday(day) ? 'primary.main' : 'text.primary' }}>
                {format(day, 'EEE')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 500, color: isToday(day) ? 'primary.main' : 'text.primary' }}>
                {format(day, 'd')}
              </Typography>
            </Box>
          ))}
        </Box>
        
        {/* Week grid */}
        <Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '80px repeat(7, 1fr)' }}>
            {/* Time column */}
            <Box sx={{ pt: 1 }}>
              {hours.map((hour) => (
                <Box
                  key={hour}
                  sx={{
                    height: 50,
                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    color: 'text.secondary'
                  }}
                >
                  {format(hour, timeFormat === '24hour' ? 'HH:mm' : 'h:mm a')}
                </Box>
              ))}
            </Box>
            
            {/* Day columns */}
            {weekDays.map((day) => (
              <Box key={day} sx={{ position: 'relative', pt: 1 }}>
                {hours.map((hour) => (
                  <Box
                    key={hour}
                    sx={{
                      height: 50,
                      borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                      borderLeft: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05)
                      }
                    }}
                    onClick={(e) => handleDayClick(setHours(day, getHours(hour)), e)}
                  />
                ))}
                
                {/* Render tasks for this day */}
                {(() => {
                  const dayTasks = tasks.filter(task => task.due_date && isSameDay(new Date(task.due_date), day));
                  const tasksByHour = {};
                  dayTasks.forEach((task, originalIndex) => {
                    const taskHour = getHours(new Date(task.due_date));
                    if (!tasksByHour[taskHour]) tasksByHour[taskHour] = [];
                    tasksByHour[taskHour].push({...task, originalIndex});
                  });

                  return Object.entries(tasksByHour)
                    .sort(([a], [b]) => parseInt(a) - parseInt(b))
                    .map(([hour, hourTasks]) => {
                      // Sort tasks within each hour by their original order
                      hourTasks.sort((a, b) => a.originalIndex - b.originalIndex);
                    const top = (parseInt(hour) * 50) + 8;
                    const maxVisible = 1;
                    const visibleTasks = hourTasks.slice(0, maxVisible);
                    const hiddenTasks = hourTasks.slice(maxVisible);
                    const hiddenCount = hiddenTasks.length;

                    return (
                      <Box key={`${day}-${hour}`} sx={{ position: 'absolute', top: `${top}px`, left: 2, right: 2, height: 50, pointerEvents: 'none' }}>
                        {visibleTasks.map((task, index) => (
                          <Box
                            key={task.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              onTaskClick(task);
                            }}
                            sx={{
                              position: 'absolute',
                                                        top: index * 35,
                          left: 0,
                          right: hiddenCount > 0 && index === maxVisible - 1 ? 50 : 0,
                          height: 32,
                          backgroundColor: priorityColors[task.priority],
                          color: 'white',
                          borderRadius: 2,
                          px: 1.5,
                          py: 1,
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                              fontWeight: 500,
                              zIndex: 1,
                              overflow: 'hidden',
                              display: 'flex',
                              alignItems: 'center',
                              pointerEvents: 'auto'
                            }}
                          >
                            {task.title}
                          </Box>
                        ))}
                        
                        {hiddenCount > 0 && (
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              // Show only the hidden tasks in the popup
                              setSelectedDayTasks(hiddenTasks);
                              setSelectedDate(day);
                              setDayTasksAnchor(e.currentTarget);
                              setDayTasksOpen(true);
                            }}
                            sx={{
                              position: 'absolute',
                              top: (maxVisible - 1) * 35,
                              right: 0,
                              width: 45,
                              height: 32,
                              backgroundColor: alpha(theme.palette.grey[600], 0.8),
                              color: 'white',
                              borderRadius: 2,
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              fontWeight: 500,
                              zIndex: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              pointerEvents: 'auto'
                            }}
                          >
                            +{hiddenCount}
                          </Box>
                        )}
                      </Box>
                    );
                  });
                })()}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderYearView = () => {
    const months = Array.from({ length: 12 }, (_, i) => new Date(currentDate.getFullYear(), i, 1));

    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 3, width: '100%' }}>
          {months.map((month) => {
            const monthStart = startOfMonth(month);
            const monthEnd = endOfMonth(monthStart);
            const startDate = startOfWeek(monthStart);
            const endDate = endOfWeek(monthEnd);
            
            const monthTasks = tasks.filter(task => 
              task.due_date && 
              new Date(task.due_date) >= monthStart && 
              new Date(task.due_date) <= monthEnd
            );

            const weeks = [];
            let day = startDate;
            
            while (day <= endDate) {
              const week = [];
              for (let i = 0; i < 7; i++) {
                week.push(day);
                day = addDays(day, 1);
              }
              weeks.push(week);
            }

            return (
              <Box
                key={month}
                sx={{
                  border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: 'white',
                  aspectRatio: '5 / 4',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: `0 1px 3px ${alpha(theme.palette.grey[400], 0.1)}`
                }}
              >
                <Box sx={{ 
                  py: 1.5, 
                  px: 1,
                  backgroundColor: alpha(theme.palette.grey[50], 0.8),
                  borderBottom: `1px solid ${alpha(theme.palette.grey[200], 0.8)}`,
                  minHeight: 'auto'
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary', fontSize: '0.875rem' }}>
                    {format(month, 'MMM')}
                  </Typography>
                </Box>
                
                <Box sx={{ p: 1.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Days header */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5, py: 0.5 }}>
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((dayName, index) => (
                      <Typography key={index} variant="caption" sx={{ textAlign: 'center', fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.2, fontWeight: 500 }}>
                        {dayName}
                      </Typography>
                    ))}
                  </Box>
                  
                  {/* Calendar grid */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {weeks.map((week, weekIndex) => (
                      <Box key={weekIndex} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', flex: 1, mb: 0.25 }}>
                        {week.map((day) => {
                          const dayTasks = monthTasks.filter(task => isSameDay(new Date(task.due_date), day));
                          
                          return (
                                                      <Box
                            key={day}
                            sx={{
                              minHeight: 32,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              borderRadius: 1,
                              mx: 0.25,
                              my: 0.125,
                              backgroundColor: !isSameMonth(day, month) 
                                ? 'transparent'
                                : isToday(day) 
                                  ? alpha(theme.palette.primary.main, 0.15)
                                  : dayTasks.length > 0
                                    ? alpha(priorityColors[dayTasks[0].priority], 0.25)
                                    : 'transparent',
                              '&:hover': {
                                backgroundColor: !isSameMonth(day, month) 
                                  ? alpha(theme.palette.grey[300], 0.1)
                                  : alpha(theme.palette.primary.main, 0.08)
                              }
                            }}
                            onClick={(e) => {
                              const dayTasks = monthTasks.filter(task => isSameDay(new Date(task.due_date), day));
                              if (dayTasks.length > 0) {
                                setSelectedDayTasks(dayTasks);
                                setSelectedDate(day);
                                setDayTasksAnchor(e.currentTarget);
                                setDayTasksOpen(true);
                              } else {
                                handleDayClick(day, e);
                              }
                            }}
                          >
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontSize: '0.75rem',
                                fontWeight: isToday(day) ? 600 : 400,
                                color: !isSameMonth(day, month) 
                                  ? alpha(theme.palette.text.disabled, 0.6)
                                  : isToday(day) 
                                    ? 'primary.main' 
                                    : 'text.primary',
                                lineHeight: 1.2
                              }}
                            >
                              {format(day, 'd')}
                            </Typography>
                          </Box>
                          );
                        })}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderMonthView = () => {
    // Keep the existing month view logic
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  const dateFormat = "d";
  const rows = [];
  let days = [];
  let day = startDate;
  let formattedDate = "";

  // Generate calendar grid
  while (day <= endDate) {
    for (let i = 0; i < 7; i++) {
      formattedDate = format(day, dateFormat);
      const cloneDay = day;
      
      // Get tasks for this day
      const dayTasks = tasks.filter(task => 
        task.due_date && isSameDay(new Date(task.due_date), day)
      );

      days.push(
        <Box
          key={day}
          sx={{
            minHeight: 120,
            border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
            p: 1,
            cursor: 'pointer',
            backgroundColor: !isSameMonth(day, monthStart) 
              ? alpha(theme.palette.grey[100], 0.5)
              : isToday(day) 
                ? alpha(theme.palette.primary.main, 0.08)
                : 'background.paper',
            transition: 'all 0.2s ease',
            '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1)
            }
          }}
                      onClick={(e) => handleDayClick(cloneDay, e)}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: isToday(day) ? 500 : 500,
              color: !isSameMonth(day, monthStart) 
                ? 'text.disabled' 
                : isToday(day) 
                  ? 'primary.main' 
                  : 'text.primary',
              mb: 1,
              fontSize: '0.9rem'
            }}
          >
            {formattedDate}
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {dayTasks.slice(0, 2).map((task) => (
              <Tooltip key={task.id} title={task.title}>
                <Chip
                  label={task.title}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onTaskClick(task);
                  }}
                  sx={{
                    height: 24,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    backgroundColor: priorityColors[task.priority],
                    color: 'white',
                    borderRadius: 2,
                    cursor: 'pointer',
                    '& .MuiChip-label': {
                      px: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%'
                    },
                    '&:hover': {
                      backgroundColor: priorityColors[task.priority],
                        opacity: 0.8
                    },
                    transition: 'all 0.2s ease'
                  }}
                />
              </Tooltip>
            ))}
            
            {dayTasks.length > 2 && (
              <Chip
                label={`+${dayTasks.length - 2} more`}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedDayTasks(dayTasks);
                  setSelectedDate(cloneDay);
                  setDayTasksAnchor(e.currentTarget);
                  setDayTasksOpen(true);
                }}
                                sx={{
                  height: 24,
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  backgroundColor: alpha(theme.palette.grey[600], 0.8),
                  color: 'white',
                  borderRadius: 2,
                  cursor: 'pointer',
                  '& .MuiChip-label': {
                    px: 1
                  },
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.grey[600], 0.9)
                  },
                  transition: 'all 0.2s ease'
                }}
              />
            )}
          </Box>
        </Box>
      );
      day = addDays(day, 1);
    }
    rows.push(
      <Box key={day} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {days}
      </Box>
    );
    days = [];
  }

    return (
      <Box sx={{ p: 0 }}>
        {rows}
      </Box>
    );
  };

    return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <CalendarContainer data-calendar-container>
      {/* Calendar Header */}
      <Box sx={{ 
        p: 3, 
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 500,
            color: 'primary.main',
            fontSize: '1.5rem'
          }}>
            {getViewTitle()}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<TodayIcon />}
              onClick={goToToday}
              sx={{
                borderRadius: 2,
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1)
                },
                transition: 'all 0.2s ease'
              }}
            >
              Today
            </Button>
            
            {/* View Selector */}
            <FormControl size="small" sx={{ minWidth: 120, ml: 2 }}>
              <InputLabel>View</InputLabel>
              <Select
                value={currentView}
                label="View"
                onChange={(e) => setCurrentView(e.target.value)}
                disableScrollLock={true}
                disablePortal={false}
                disableAutoFocus={true}
                disableEnforceFocus={true}
                MenuProps={{
                  disableScrollLock: true,
                  disablePortal: false,
                  disableAutoFocus: true,
                  disableEnforceFocus: true,
                  PaperProps: {
                    sx: {
                      position: 'absolute !important',
                      minWidth: 120,
                      contain: 'layout style paint',
                      zIndex: 9999
                    }
                  },
                  sx: {
                    zIndex: 9999
                  }
                }}
                sx={{
                  borderRadius: 2,
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: alpha(theme.palette.grey[400], 0.8)
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: theme.palette.primary.main
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: theme.palette.primary.main
                  }
                }}
              >
                <MenuItem value="day">Day</MenuItem>
                <MenuItem value="week">Week</MenuItem>
                <MenuItem value="month">Month</MenuItem>
                <MenuItem value="year">Year</MenuItem>
              </Select>
            </FormControl>
            
            <IconButton 
              onClick={navigatePrev}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                backgroundColor: 'white',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  borderColor: theme.palette.primary.main
                },
                transition: 'all 0.2s ease'
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            
            <IconButton 
              onClick={navigateNext}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                backgroundColor: 'white',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  borderColor: theme.palette.primary.main
                },
                transition: 'all 0.2s ease'
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Days of Week Header - Only for Month View */}
        {currentView === 'month' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <Box
              key={day}
              sx={{
                p: 1.5,
                textAlign: 'center',
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                backgroundColor: alpha(theme.palette.primary.main, 0.05)
              }}
            >
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 500, 
                color: 'primary.main',
                fontSize: '0.875rem'
              }}>
                {day}
              </Typography>
            </Box>
          ))}
        </Box>
        )}
      </Box>

      {/* Calendar Content */}
      {renderCalendarContent()}

      {/* Legend */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: 3, 
        p: 3,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.8)}, ${alpha(theme.palette.grey[100], 0.4)})`,
        borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
      }}>
        {Object.entries(priorityColors).map(([priority, color]) => (
          <Box key={priority} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                backgroundColor: color,
                borderRadius: '50%',
                border: `2px solid ${alpha(color, 0.3)}`,
                boxShadow: `0 2px 8px ${alpha(color, 0.3)}`
              }}
            />
            <Typography variant="caption" sx={{ 
              textTransform: 'capitalize',
              fontWeight: 500,
              color: 'text.secondary',
              fontSize: '0.8rem'
            }}>
              {priority}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Day Tasks Popover */}
      <Popover
        open={dayTasksOpen}
        anchorEl={dayTasksAnchor}
        onClose={handleDayTasksClose}
        disableScrollLock={true}
        disablePortal={false}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            position: 'absolute !important',
            minWidth: 300,
            maxWidth: 400,
            borderRadius: 2,
            boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`,
            border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
            contain: 'layout style paint',
            zIndex: 9999
          }
        }}
        sx={{
          zIndex: 9999
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
            </Typography>
            
            {selectedDayTasks.length > 1 && (
              <Button
                onClick={toggleBulkDeleteMode}
                variant={bulkDeleteMode ? "contained" : "outlined"}
                size="small"
                color={bulkDeleteMode ? "error" : "primary"}
                sx={{ borderRadius: 2, fontWeight: 500, fontSize: '0.8rem' }}
              >
                {bulkDeleteMode ? 'Cancel' : 'Bulk Delete'}
              </Button>
            )}
          </Box>
          
          {bulkDeleteMode && selectedDayTasks.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, p: 1, backgroundColor: alpha(theme.palette.grey[100], 0.5), borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox
                  checked={selectedTasksForDelete.length === selectedDayTasks.length}
                  indeterminate={selectedTasksForDelete.length > 0 && selectedTasksForDelete.length < selectedDayTasks.length}
                  onChange={selectAllTasks}
                  size="small"
                />
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                  Select All ({selectedDayTasks.length})
                </Typography>
              </Box>
              
              {selectedTasksForDelete.length > 0 && (
                <Button
                  onClick={handleBulkDelete}
                  variant="contained"
                  color="error"
                  size="small"
                  sx={{ borderRadius: 2, fontWeight: 500, fontSize: '0.8rem' }}
                >
                  <DeleteIcon sx={{ mr: 0.5, fontSize: 16 }} />
                  Delete ({selectedTasksForDelete.length})
                </Button>
              )}
            </Box>
          )}
          
          <List sx={{ py: 0 }}>
            {selectedDayTasks.map((task, index) => (
              <Box key={task.id}>
                <ListItem 
                  sx={{ 
                    px: 0, 
                    py: 1,
                    cursor: 'pointer',
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.grey[100], 0.5)
                    }
                  }}
                  onClick={() => {
                    if (!bulkDeleteMode) {
                      handleDayTasksClose();
                      onTaskClick(task);
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {bulkDeleteMode && (
                      <Checkbox
                        checked={selectedTasksForDelete.includes(task.id)}
                        onChange={() => toggleTaskSelection(task.id)}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: priorityColors[task.priority],
                        borderRadius: '50%',
                        flexShrink: 0
                      }}
                    />
                    <ListItemText 
                      primary={task.title}
                      secondary={task.due_date ? format(new Date(task.due_date), timeFormat === '24hour' ? 'HH:mm' : 'h:mm a') : ''}
                      primaryTypographyProps={{
                        sx: { fontSize: '0.9rem', fontWeight: 500 }
                      }}
                      secondaryTypographyProps={{
                        sx: { fontSize: '0.75rem', color: 'text.secondary' }
                      }}
                    />
                  </Box>
                </ListItem>
                {index < selectedDayTasks.length - 1 && <Divider sx={{ my: 0.5 }} />}
              </Box>
            ))}
          </List>
          
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Typography sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>+</Typography>}
            onClick={handleCreateFromPopover}
            sx={{
              mt: 2,
              borderRadius: 2,
              fontWeight: 500,
              color: 'primary.main',
              borderColor: alpha(theme.palette.primary.main, 0.5),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderColor: theme.palette.primary.main
              }
            }}
          >
            Create
          </Button>
        </Box>
      </Popover>

      {/* Quick Add Dialog */}
      <Dialog
        open={quickAddOpen}
        onClose={handleQuickAddClose}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: alpha(theme.palette.grey[900], 0.4) }
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: 400,
            maxWidth: 500,
            mx: 'auto'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500, 
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
          py: 3,
          fontSize: '1.25rem'
        }}>
          Add Task for {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
        </DialogTitle>
        <DialogContent sx={{ pt: 6, pb: 3, px: 4 }}>
          <TextField
            autoFocus
            fullWidth
            label="Task Title"
            placeholder="Enter task title..."
            value={quickTaskTitle}
            onChange={(e) => setQuickTaskTitle(e.target.value)}
            sx={{ 
              mb: 4,
              mt: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                minHeight: 56,
                backgroundColor: alpha(theme.palette.grey[50], 0.5)
              },
              '& .MuiInputLabel-root': {
                fontWeight: 500
              }
            }}
          />
          
          <DateTimePicker
            label="Due Date & Time"
            sx={{
              borderRadius: 3,
              minHeight: 56,
              backgroundColor: alpha(theme.palette.grey[50], 0.5),
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                minHeight: 56,
                backgroundColor: alpha(theme.palette.grey[50], 0.5)
              },
              '& .MuiInputLabel-root': {
                fontWeight: 500
              }
            }}
            value={quickTaskDeadline}
            onChange={(newValue) => setQuickTaskDeadline(newValue)}
            format={timeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A'}
            ampm={timeFormat !== '24hour'}
            renderInput={(params) => <TextField {...params} fullWidth sx={{ 
              mb: 6,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                minHeight: 56,
                backgroundColor: alpha(theme.palette.grey[50], 0.5)
              },
              '& .MuiInputLabel-root': {
                fontWeight: 500
              }
            }} />}
          />
          
          <FormControl fullWidth sx={{mt: 4}}>
            <InputLabel sx={{ fontWeight: 500 }}>Priority</InputLabel>
            <Select
              value={quickTaskPriority}
              label="Priority"
              onChange={(e) => setQuickTaskPriority(e.target.value)}
              sx={{ 
                borderRadius: 3,
                minHeight: 56,
                backgroundColor: alpha(theme.palette.grey[50], 0.5),
                '& .MuiOutlinedInput-root': {
                  minHeight: 56
                }
              }}
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 4, pt: 2, gap: 2, justifyContent: 'flex-end' }}>
          <Button 
            onClick={handleQuickAddClose} 
            variant="outlined"
            sx={{ 
              borderRadius: 3, 
              fontWeight: 500,
              px: 3,
              py: 1.5,
              minWidth: 100,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleQuickAdd} 
            variant="contained" 
            disabled={!quickTaskTitle.trim()}
            sx={{ 
              borderRadius: 3, 
              fontWeight: 500,
              px: 3,
              py: 1.5,
              minWidth: 120,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
              },
              '&:disabled': {
                opacity: 0.6,
                boxShadow: 'none'
              }
            }}
          >
            Add Task
          </Button>
        </DialogActions>
      </Dialog>
    </CalendarContainer>
    </LocalizationProvider>
  );
};

export default CalendarView;
